import React, { useState } from 'react';
import ToolPage from '@/components/ToolPage';
import { Field, inputClass, ResultRow } from '@/components/calcUi';
import { getToolBySlug } from '@/lib/tools';
import { formatNumber } from '@/lib/currency';

const num = (v) => (v === '' || v === null || v === undefined || isNaN(Number(v)) ? NaN : Number(v));

// ========================= Percentage Calculator =========================
export function PercentageCalculator() {
  const tool = getToolBySlug('percentage-calculator');
  const [mode, setMode] = useState('pct-of'); // pct-of | x-of-y | reverse
  const [a, setA] = useState('20');
  const [b, setB] = useState('150');

  const A = num(a), B = num(b);
  let result = NaN, expression = '';
  if (mode === 'pct-of' && !isNaN(A) && !isNaN(B)) { result = (A / 100) * B; expression = `${A}% of ${B}`; }
  if (mode === 'x-of-y' && !isNaN(A) && !isNaN(B) && B !== 0) { result = (A / B) * 100; expression = `${A} is what % of ${B}`; }
  if (mode === 'reverse' && !isNaN(A) && !isNaN(B) && B !== 0) { result = A / (B / 100); expression = `${A} is ${B}% of what?`; }

  return (
    <ToolPage
      tool={tool}
      intro={<p>A quick answer to the three most common percentage questions people ask every day.</p>}
      howItWorks={<p>Choose the question type, enter the two known values, and the answer appears instantly. All math happens in your browser — nothing is sent anywhere.</p>}
      formula={<>
        <div>Percent of a number:  result = (X / 100) × Y</div>
        <div>What percent A is of B:  result = (A / B) × 100</div>
        <div>Reverse — A is X% of what:  result = A / (X / 100)</div>
      </>}
      examples={[
        '20% of 150 equals 30. Use this to find a 20% discount on a $150 shirt.',
        '45 is what percent of 200? Answer: 22.5% — helpful for grading a quiz score.',
        '30 is 15% of what? Answer: 200 — useful when reverse-engineering a sale price.',
      ]}
      faqs={[
        { q: 'How do I calculate percentages in my head?', a: 'For X% of Y, break X into 10% chunks: 10% of Y is Y/10. Then scale up or down. For example, 30% of 80 = 3 × 8 = 24.' },
        { q: 'Is percent the same as per hundred?', a: 'Yes. "Per cent" literally means "per hundred" in Latin, so 25% means 25 out of every 100.' },
        { q: 'Can percentages be over 100?', a: 'Absolutely — 200% of 50 is 100. Growth figures and returns are often expressed above 100%.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Field label="Question type">
            <div className="flex flex-col gap-2 text-sm">
              {[
                { id: 'pct-of', label: 'What is X% of Y?' },
                { id: 'x-of-y', label: 'X is what percent of Y?' },
                { id: 'reverse', label: 'X is Y% of what number?' },
              ].map((o) => (
                <label key={o.id} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="pctmode" value={o.id} checked={mode === o.id} onChange={() => setMode(o.id)} data-testid={`pct-mode-${o.id}`} className="accent-emerald-600" />
                  {o.label}
                </label>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={mode === 'pct-of' ? 'X (%)' : mode === 'x-of-y' ? 'A' : 'A'} htmlFor="pct-a">
              <input id="pct-a" data-testid="pct-input-a" type="number" step="any" value={a} onChange={(e) => setA(e.target.value)} className={inputClass} />
            </Field>
            <Field label={mode === 'pct-of' ? 'Y' : mode === 'x-of-y' ? 'B' : 'B (%)'} htmlFor="pct-b">
              <input id="pct-b" data-testid="pct-input-b" type="number" step="any" value={b} onChange={(e) => setB(e.target.value)} className={inputClass} />
            </Field>
          </div>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{expression || 'Result'}</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="pct-result">
            {isNaN(result) ? '—' : formatNumber(result, mode === 'x-of-y' ? 2 : 4)}
            {mode === 'x-of-y' && !isNaN(result) ? '%' : ''}
          </div>
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Percentage Change =========================
export function PercentageChangeCalculator() {
  const tool = getToolBySlug('percentage-change-calculator');
  const [oldV, setOldV] = useState('80');
  const [newV, setNewV] = useState('100');
  const o = num(oldV), n = num(newV);
  const valid = !isNaN(o) && !isNaN(n) && o !== 0;
  const change = valid ? ((n - o) / Math.abs(o)) * 100 : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Compare two numbers — a price, a score, a website visitor count — and get the direction and magnitude of change.</p>}
      formula={<>Percentage change = ((New − Old) / |Old|) × 100</>}
      examples={[
        'Old price $80, new price $100 → 25% increase.',
        'Traffic falls from 1,200 to 900 visits → −25% change (a 25% decrease).',
      ]}
      faqs={[
        { q: 'What if the old value is zero?', a: 'Percentage change is undefined when the starting value is zero — you cannot divide by zero. Report the raw difference instead.' },
        { q: 'Why use absolute value?', a: 'It keeps the sign of the result meaningful (positive = growth, negative = decline), even when the old value is negative.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label="Original value" htmlFor="pchg-old">
            <input id="pchg-old" data-testid="pchg-old" type="number" step="any" value={oldV} onChange={(e) => setOldV(e.target.value)} className={inputClass} />
          </Field>
          <Field label="New value" htmlFor="pchg-new">
            <input id="pchg-new" data-testid="pchg-new" type="number" step="any" value={newV} onChange={(e) => setNewV(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <div className="result-panel rounded-xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Percentage change</div>
          <div className="text-4xl font-bold" data-testid="pchg-result">
            <span className={valid ? (change >= 0 ? 'text-emerald-700' : 'text-rose-600') : 'text-slate-400'}>
              {valid ? `${change >= 0 ? '+' : ''}${formatNumber(change, 2)}%` : '—'}
            </span>
          </div>
          {valid && (
            <div className="mt-3 text-sm text-slate-600">
              Absolute change: {formatNumber(n - o, 2)}
            </div>
          )}
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Fractions Calculator =========================
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; }
function simplify(n, d) { if (d === 0) return { n: NaN, d: NaN }; if (d < 0) { n = -n; d = -d; } const g = gcd(n, d); return { n: n / g, d: d / g }; }

export function FractionsCalculator() {
  const tool = getToolBySlug('fractions-calculator');
  const [n1, setN1] = useState('1'); const [d1, setD1] = useState('2');
  const [n2, setN2] = useState('1'); const [d2, setD2] = useState('3');
  const [op, setOp] = useState('+');
  const N1 = parseInt(n1), D1 = parseInt(d1), N2 = parseInt(n2), D2 = parseInt(d2);
  const valid = ![N1,D1,N2,D2].some(Number.isNaN) && D1 !== 0 && D2 !== 0 && !(op === '÷' && N2 === 0);
  let res = { n: NaN, d: NaN };
  if (valid) {
    if (op === '+') res = simplify(N1 * D2 + N2 * D1, D1 * D2);
    if (op === '−') res = simplify(N1 * D2 - N2 * D1, D1 * D2);
    if (op === '×') res = simplify(N1 * N2, D1 * D2);
    if (op === '÷') res = simplify(N1 * D2, D1 * N2);
  }
  const decimal = valid && res.d ? res.n / res.d : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Add, subtract, multiply or divide two fractions. Answers are automatically reduced to lowest terms.</p>}
      formula={<>a/b ± c/d = (a·d ± c·b) / (b·d),  a/b × c/d = (a·c) / (b·d),  a/b ÷ c/d = (a·d) / (b·c)</>}
      examples={[
        '1/2 + 1/3 = 5/6 (≈ 0.833).',
        '3/4 × 2/5 = 6/20, which simplifies to 3/10.',
      ]}
      faqs={[
        { q: 'How do you find a common denominator?', a: 'The simplest common denominator is the product of the two denominators. For a smaller number, use the least common multiple (LCM).' },
        { q: 'What is a "reduced" fraction?', a: 'A fraction reduced to lowest terms has no common factor other than 1 in its numerator and denominator (e.g., 6/8 reduces to 3/4).' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <FracInput labelTop="a" labelBot="b" nId="frac-n1" dId="frac-d1" n={n1} d={d1} setN={setN1} setD={setD1} testPrefix="f1" />
            <select value={op} onChange={(e) => setOp(e.target.value)} data-testid="frac-op" className={inputClass + ' w-14 text-center font-semibold'}>
              {['+','−','×','÷'].map((o) => <option key={o}>{o}</option>)}
            </select>
            <FracInput labelTop="c" labelBot="d" nId="frac-n2" dId="frac-d2" n={n2} d={d2} setN={setN2} setD={setD2} testPrefix="f2" />
          </div>
        </div>
        <div className="result-panel rounded-xl p-6 flex flex-col justify-center">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Result</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="frac-result">
            {valid ? `${res.n}/${res.d}` : '—'}
          </div>
          <div className="mt-2 text-sm text-slate-600" data-testid="frac-decimal">
            {valid ? `≈ ${formatNumber(decimal, 4)}` : ''}
          </div>
        </div>
      </div>
    </ToolPage>
  );
}
function FracInput({ n, d, setN, setD, nId, dId, labelTop, labelBot, testPrefix }) {
  return (
    <div className="flex flex-col items-center">
      <input aria-label={labelTop} id={nId} data-testid={`${testPrefix}-num`} type="number" value={n} onChange={(e) => setN(e.target.value)} className={inputClass + ' w-20 text-center'} />
      <div className="h-px w-16 bg-slate-300 my-1.5"></div>
      <input aria-label={labelBot} id={dId} data-testid={`${testPrefix}-den`} type="number" value={d} onChange={(e) => setD(e.target.value)} className={inputClass + ' w-20 text-center'} />
    </div>
  );
}
